import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../API/API";
import { BASE_URL } from "../API/Constants";
import { getImageUrl } from "../utils/imageHelper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const api = new API();

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', contrastText: '#ffffff' },
    secondary: { main: '#424242', contrastText: '#ffffff' },
    error: { main: '#d32f2f' },
    warning: { main: '#ed6c02' },
    info: { main: '#0288d1' },
    success: { main: '#2e7d32' },
    background: { default: '#f8f9fa', paper: '#ffffff' },
    text: { primary: '#212529', secondary: '#6c757d' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 6, fontWeight: 600 },
        containedPrimary: { backgroundColor: '#1976d2', color: '#ffffff', '&:hover': { backgroundColor: '#115293' } },
        outlinedPrimary: { borderColor: '#1976d2', color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#1976d2' } },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundImage: 'none' }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 6 }
      }
    }
  }
});

export default function GuestImages() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    phone_number: "",
    room_number: "",
    guest_name: "",
    company_name: "",
    booking_id: "",
    status: "waiting",
  });

  // New images being uploaded
  const [images, setImages] = useState([]);
  // Existing images on the server (for editing)
  const [existingImages, setExistingImages] = useState([]);
  // Past records found for the entered phone number
  const [pastRecords, setPastRecords] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const [loading, setLoading] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [fetchingRooms, setFetchingRooms] = useState(false);
  const [autoLookupLoading, setAutoLookupLoading] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneCheckMessage, setPhoneCheckMessage] = useState("");
  const [phoneCheckType, setPhoneCheckType] = useState(""); // "success" or "error"

  // Edit State
  const [editId, setEditId] = useState(null);

  // Image Preview Modal State
  const [openImage, setOpenImage] = useState(null);

  // Upload Source Selection Dialog State
  const [openSourceDialog, setOpenSourceDialog] = useState(false);

  // Refs for hidden inputs
  const cameraInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);

  useEffect(() => {
    fetchAvailableRooms();
  }, []);

  // Handle edit item passed via location.state
  useEffect(() => {
    if (location.state && location.state.editItem) {
      const item = location.state.editItem;
      setEditId(item.id);
      const rmStr = item.room_number ? String(item.room_number) : "";
      const phoneStr = item.phone_number ? String(item.phone_number) : "";
      setFormData({
        phone_number: phoneStr,
        room_number: rmStr,
        guest_name: item.guest_name || "",
        company_name: item.company_name || "",
        booking_id: item.booking_id || "",
        status: item.status || "waiting",
      });
      setImages([]);
      setExistingImages(item.images || []);

      // Add room to available rooms if not present
      if (rmStr) {
        setAvailableRooms((prev) => {
          const exists = prev.some((r) => String(r.room_number) === rmStr);
          if (!exists) {
            return [{ room_number: rmStr, id: `auto-${rmStr}` }, ...prev];
          }
          return prev;
        });
      }

      // Automatically fetch past records for this phone number so history table and drag-and-drop images show in Edit mode too!
      if (phoneStr.length === 10) {
        handlePhoneAutoLookup(phoneStr, true);
        handleCheckPhone(phoneStr);
      }
    }
  }, [location.state]);

  const fetchAvailableRooms = async () => {
    setFetchingRooms(true);
    try {
      const response = await api.get("api/guest-images/available-rooms");
      if (response && response.data) {
        let rawList = [];
        const resData = response.data;
        if (Array.isArray(resData)) {
          rawList = resData;
        } else if (resData.data && Array.isArray(resData.data)) {
          rawList = resData.data;
        } else if (resData.rooms && Array.isArray(resData.rooms)) {
          rawList = resData.rooms;
        } else if (resData.available_rooms && Array.isArray(resData.available_rooms)) {
          rawList = resData.available_rooms;
        }

        const rooms = rawList.map((item) => {
          if (typeof item === 'object' && item !== null) {
            return {
              ...item,
              room_number: String(item.room_number || item.room_no || item.room_name || item.number || item.id || '')
            };
          }
          return { room_number: String(item) };
        }).filter(r => r.room_number !== '');

        setAvailableRooms((prev) => {
          const currentEditRoom = location.state?.editItem?.room_number || formData.room_number;
          if (currentEditRoom) {
            const roomStr = String(currentEditRoom);
            const exists = rooms.some((r) => String(r.room_number) === roomStr);
            if (!exists) {
              return [{ room_number: roomStr, id: `auto-${roomStr}` }, ...rooms];
            }
          }
          return rooms;
        });
      }
    } catch (error) {
      console.error("Error fetching available rooms:", error);
    } finally {
      setFetchingRooms(false);
    }
  };

  const handleCheckPhone = async (phone) => {
    if (!phone) {
      setPhoneCheckMessage("");
      setPhoneCheckType("");
      return;
    }
    setPhoneChecking(true);
    setPhoneCheckMessage("");
    setPhoneCheckType("");
    try {
      const response = await api.get(`api/guest-images/check-phone?phone_number=${phone}`, null, true);
      if (response && response.data) {
        const resData = response.data;
        const msg = resData.message || resData.error || (typeof resData.errors === 'string' ? resData.errors : "");
        const msgLower = (msg || "").toLowerCase();

        const isError = resData.status === false || resData.success === false || resData.error || msgLower.includes("already") || msgLower.includes("exist") || msgLower.includes("invalid") || msgLower.includes("taken");
        const isAvailable = resData.status === true || resData.success === true || msgLower.includes("available") || msgLower.includes("valid") || msgLower.includes("not found");

        if (msg) {
          setPhoneCheckMessage(msg);
          if (isAvailable && !isError) {
            setPhoneCheckType("success");
          } else if (isError) {
            setPhoneCheckType("error");
          } else {
            setPhoneCheckType(msgLower.includes("available") ? "success" : "error");
          }
        } else if (resData.status === false) {
          setPhoneCheckMessage("Phone number check failed.");
          setPhoneCheckType("error");
        } else {
          setPhoneCheckMessage("");
          setPhoneCheckType("");
        }
      }
    } catch (error) {
      console.error("Error checking phone number:", error);
      const errRes = error?.response?.data;
      if (errRes) {
        const msg = errRes.message || errRes.error || errRes.errors;
        let msgStr = "";
        if (typeof msg === 'object' && msg !== null) {
          const firstVal = Object.values(msg)[0];
          msgStr = Array.isArray(firstVal) ? firstVal[0] : String(firstVal);
        } else if (msg) {
          msgStr = String(msg);
        }

        if (msgStr) {
          setPhoneCheckMessage(msgStr);
          if (msgStr.toLowerCase().includes("available")) {
            setPhoneCheckType("success");
          } else {
            setPhoneCheckType("error");
          }
        } else {
          setPhoneCheckMessage("Phone check error occurred.");
          setPhoneCheckType("error");
        }
      } else {
        setPhoneCheckMessage("Failed to check phone number.");
        setPhoneCheckType("error");
      }
    } finally {
      setPhoneChecking(false);
    }
  };

  const getStatusChipStyle = (statusStr) => {
    const status = (statusStr || '').toLowerCase().replace(/[^a-z]/g, '');
    if (status.includes('checkin') || status.includes('checkedin') || status.includes('completed') || status.includes('approved')) {
      return {
        bgcolor: '#d32f2f', // Red background for CHECKED-IN
        color: '#ffffff',
        fontWeight: 700,
      };
    }
    if (status.includes('waiting') || status.includes('pending')) {
      return {
        bgcolor: '#1976d2', // Blue background for WAITING
        color: '#ffffff',
        fontWeight: 700,
      };
    }
    return {
      bgcolor: '#757575',
      color: '#ffffff',
      fontWeight: 700,
    };
  };

  const isWaitingStatus = (statusStr) => {
    const status = (statusStr || '').toLowerCase().replace(/[^a-z]/g, '');
    return status.includes('waiting') || status.includes('pending') || status === '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Auto-lookup guest data when 10-digit phone is entered or editing
  const handlePhoneAutoLookup = async (phone, isEdit = false) => {
    setAutoLookupLoading(true);
    try {
      const res = await api.get(`api/guest-images?phone_number=${phone}`);
      const records = res?.data?.data || res?.data || [];
      const list = Array.isArray(records) ? records : [];
      setPastRecords(list);

      if (list.length > 0) {
        const latestRec = list[0];
        setFormData((prev) => ({
          ...prev,
          guest_name: prev.guest_name || latestRec.guest_name || "",
          company_name: prev.company_name || latestRec.company_name || "",
          booking_id: prev.booking_id || latestRec.booking_id || "",
        }));

        if (!isEdit) {
          setExistingImages([]); // Start empty for new entry so user can drag or click to select!
        }
        toast.info(
          `✅ Found ${list.length} previous submission(s). Click or drag images down to add!`,
          { autoClose: 3500 }
        );
      } else {
        if (!isEdit) {
          setExistingImages([]);
        }
        toast.info("No previous record found for this number.", { autoClose: 2000 });
      }
    } catch (e) {
      // silently ignore lookup errors
    } finally {
      setAutoLookupLoading(false);
    }
  };

  const addExistingImage = (img) => {
    const currentTotal = images.length + existingImages.length;
    if (currentTotal >= 6) {
      toast.warning("Maximum 6 images allowed!", { autoClose: 2000 });
      return;
    }
    if (!existingImages.includes(img)) {
      setExistingImages(prev => [...prev, img]);
      toast.success("Image added to submission!", { autoClose: 1500 });
    } else {
      toast.info("Image already added.", { autoClose: 1200 });
    }
  };

  const addAllExistingImages = () => {
    const currentTotal = images.length + existingImages.length;
    if (currentTotal >= 6) {
      toast.warning("Maximum 6 images allowed!", { autoClose: 2000 });
      return;
    }
    const all = [...existingImages];
    let added = 0;
    pastRecords.forEach(rec => {
      if (rec.images && Array.isArray(rec.images)) {
        rec.images.forEach(img => {
          if (img && !all.includes(img) && (images.length + all.length) < 6) {
            all.push(img);
            added++;
          }
        });
      }
    });
    setExistingImages(all);
    if (added > 0) {
      if (images.length + all.length >= 6) {
        toast.info("Added past images (reached max limit of 6).", { autoClose: 2000 });
      } else {
        toast.success("Added past images to submission!", { autoClose: 1500 });
      }
    } else {
      toast.info("No more images could be added (max 6 allowed).", { autoClose: 1500 });
    }
  };

  const handleDragStart = (e, img) => {
    e.dataTransfer.setData("text/plain", img);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (images.length + existingImages.length >= 6) {
      toast.warning("Maximum 6 images allowed!", { autoClose: 2000 });
      return;
    }
    const img = e.dataTransfer.getData("text/plain");
    if (img) {
      addExistingImage(img);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const currentTotal = images.length + existingImages.length;
    const remaining = 6 - currentTotal;

    if (remaining <= 0) {
      toast.warning("Maximum 6 images allowed!", { autoClose: 2000 });
      return;
    }

    if (newFiles.length > remaining) {
      toast.warning(`Only ${remaining} image(s) added. Maximum limit is 6 images.`, { autoClose: 2500 });
      setImages(prev => [...prev, ...newFiles.slice(0, remaining)]);
    } else {
      setImages(prev => [...prev, ...newFiles]);
    }
  };

  const removeSelectedImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setEditId(null);
    setFormData({
      phone_number: "",
      room_number: "",
      guest_name: "",
      company_name: "",
      booking_id: "",
      status: "waiting",
    });
    setImages([]);
    setExistingImages([]);
    setPastRecords([]);
    setPhoneCheckMessage("");
    setPhoneCheckType("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append("phone_number", formData.phone_number);
      data.append("room_number", formData.room_number);
      if (formData.guest_name) data.append("guest_name", formData.guest_name);
      if (formData.company_name) data.append("company_name", formData.company_name);
      if (formData.booking_id) data.append("booking_id", formData.booking_id);
      data.append("status", formData.status);

      // Add newly uploaded file objects
      if (images.length > 0) {
        images.forEach((image) => {
          data.append("images[]", image);
        });
      }

      if (existingImages.length > 0) {
        data.append("existing_images", JSON.stringify(existingImages));

        // For new submissions, convert dragged/selected existing images into File Blobs so backend saves them in images[]
        if (!editId) {
          for (let i = 0; i < existingImages.length; i++) {
            const imgPath = existingImages[i];
            try {
              const fullUrl = getImageUrl(imgPath);
              const res = await fetch(fullUrl);
              const blob = await res.blob();
              const filename = imgPath.split('/').pop() || `existing_image_${i}.jpg`;
              const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
              data.append("images[]", file);
            } catch (err) {
              console.warn("Error converting existing image to file blob:", imgPath, err);
            }
          }
        }
      }

      data.isMultipart = true;

      let response;
      if (editId) {
        data.append("_method", "PUT");
        response = await api.post(`api/guest-images/${editId}`, data);
      } else {
        response = await api.post("api/guest-images", data);
      }

      if (response) {
        toast.success(editId ? "Guest images updated successfully!" : "Guest images saved successfully!");
        resetForm();
        navigate("/saved-submissions");
      }
    } catch (error) {
      console.error("Error saving guest images:", error);
      toast.error("Failed to save guest images.");
    } finally {
      setLoading(false);
    }
  };



  const totalImagesCount = images.length + existingImages.length;

  const isFormValid =
    formData.phone_number.length === 10 &&
    Boolean(formData.room_number) &&
    totalImagesCount > 0 &&
    totalImagesCount <= 6;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', mt: { xs: 0.5, md: 2 }, mb: 3, px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#1565c0',
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              letterSpacing: 0.3
            }}
          >
            {editId ? "Edit Guest Entry" : "Add Guest Entry"}
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FormatListBulletedIcon />}
            onClick={() => navigate("/saved-submissions")}
            sx={{
              fontSize: isMobile ? '0.78rem' : '0.875rem',
              py: isMobile ? 0.6 : 0.8,
              px: isMobile ? 1.2 : 2,
              borderRadius: 1.5,
              fontWeight: 600
            }}
          >
            Saved Submissions
          </Button>
        </Box>

        {/* Form Container Card */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: '1px solid #d0d7de',
              borderRadius: 2,
              width: '100%',
              maxWidth: { xs: '100%', md: 550 }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.1rem' }, color: '#24292f' }}>
                {editId ? "Update Existing Entry" : "Add New Guest Details"}
              </Typography>
              {editId && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  startIcon={<ArrowBackIcon sx={{ fontSize: '0.9rem !important' }} />}
                  onClick={() => { resetForm(); navigate("/saved-submissions"); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                >
                  Cancel Edit
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box component="form" onSubmit={handleSubmit}>
              {(() => {
                const isLocked = Boolean(editId) && formData.status?.toLowerCase() !== "waiting";
                return (
                  <React.Fragment>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      name="phone_number"
                      value={formData.phone_number}
                      disabled={isLocked}
                      error={phoneCheckType === "error"}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        if (val.length <= 10) {
                          handleInputChange({ target: { name: 'phone_number', value: val } });
                          setPhoneCheckMessage("");
                          setPhoneCheckType("");
                          if (val.length === 10) {
                            handleCheckPhone(val);
                            handlePhoneAutoLookup(val, Boolean(editId));
                          } else if (val.length < 10) {
                            if (!editId) setExistingImages([]);
                            setPastRecords([]);
                          }
                        }
                      }}
                      onClick={() => {
                        if (formData.phone_number) {
                          handleCheckPhone(formData.phone_number);
                        }
                        if (formData.phone_number.length === 10 && pastRecords.length === 0) {
                          handlePhoneAutoLookup(formData.phone_number, Boolean(editId));
                        }
                      }}
                      onBlur={() => {
                        if (formData.phone_number) {
                          handleCheckPhone(formData.phone_number);
                        }
                      }}
                      margin="dense"
                      required
                      variant="outlined"
                      placeholder="10 digit number"
                      inputProps={{ maxLength: 10, inputMode: 'numeric', style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                      InputLabelProps={{ style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                      InputProps={{
                        endAdornment: (phoneChecking || autoLookupLoading) ? (
                          <CircularProgress size={14} sx={{ mr: 1 }} />
                        ) : null,
                      }}
                      helperText={
                        phoneChecking
                          ? "Checking phone..."
                          : autoLookupLoading
                            ? "Looking up guest..."
                            : phoneCheckMessage
                              ? ""
                              : formData.phone_number.length === 10
                                ? "✓ Phone number complete"
                                : `${formData.phone_number.length}/10 digits`
                      }
                      FormHelperTextProps={{ style: { fontSize: '0.7rem', marginTop: 2 } }}
                      sx={{ mb: phoneCheckMessage ? 0.5 : 1.5 }}
                    />

                    {phoneCheckMessage && (
                      <Box
                        sx={{
                          color: phoneCheckType === "success" ? '#2e7d32' : '#d32f2f',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          mt: 0,
                          mb: 1.5,
                          p: 1,
                          bgcolor: phoneCheckType === "success" ? '#e8f5e9' : '#fde8e8',
                          border: `1px solid ${phoneCheckType === "success" ? '#a5d6a7' : '#f8b4b4'}`,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8
                        }}
                      >
                        {phoneCheckType === "success" ? "✓" : "⚠️"} {phoneCheckMessage}
                      </Box>
                    )}

                    <Autocomplete
                      fullWidth
                      size="small"
                      disabled={isLocked}
                      options={availableRooms}
                      getOptionLabel={(option) => {
                        if (!option) return "";
                        if (typeof option === 'string' || typeof option === 'number') return String(option);
                        return option.room_number ? String(option.room_number) : "";
                      }}
                      value={
                        availableRooms.find(r => String(r.room_number || r) === String(formData.room_number)) ||
                        (formData.room_number ? { room_number: String(formData.room_number) } : null)
                      }
                      onChange={(event, newValue) => {
                        const val = newValue ? (newValue.room_number ? String(newValue.room_number) : String(newValue)) : "";
                        setFormData(prev => ({ ...prev, room_number: val }));
                      }}
                      onOpen={() => {
                        fetchAvailableRooms();
                      }}
                      loading={fetchingRooms}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Room Number"
                          margin="dense"
                          required
                          variant="outlined"
                          onClick={() => {
                            fetchAvailableRooms();
                          }}
                          inputProps={{ ...params.inputProps, style: { ...params.inputProps.style, fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                          InputLabelProps={{ style: { fontSize: isMobile ? '0.85rem' : '0.95rem' } }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {fetchingRooms ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                      sx={{ mb: 1.5 }}
                    />


                  </React.Fragment>
                );
              })()}

              {/* Previous Submissions Table for entered Phone Number */}
              {pastRecords.length > 0 && (
                <Box sx={{ mt: 1.5, mb: 2, border: '1px solid #d0d7de', borderRadius: 2, overflow: 'hidden', bgcolor: '#ffffff' }}>
                  <Box sx={{ bgcolor: '#f4f6f8', px: 1.5, py: 0.8, borderBottom: '1px solid #d0d7de', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1565c0' }}>
                      📋 Saved Submissions ({pastRecords.length})
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: { xs: 'none', sm: 'inline' } }}>
                        💡 Click or drag image to add
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={addAllExistingImages}
                        sx={{ fontSize: '0.68rem', py: 0.2, px: 0.8, textTransform: 'none', borderRadius: 1, fontWeight: 700 }}
                      >
                        + Add All
                      </Button>
                    </Box>
                  </Box>
                  <TableContainer sx={{ maxH: 220, overflowY: 'auto' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>MOBILE NO.</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>GUEST NAME</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>COMPANY</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>BOOKING ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>ROOM</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>DATE & TIME</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>STATUS</TableCell>
                          <TableCell sx={{ fontWeight: 700, py: 0.8, px: 1.2, color: '#24292f', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>IMAGES</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pastRecords.map((rec, index) => (
                          <TableRow key={index} hover sx={{ bgcolor: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1f2937', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {rec.phone_number}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#1f2937', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {isWaitingStatus(rec.status) ? '-' : (rec.guest_name || '-')}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#4b5563', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {isWaitingStatus(rec.status) ? '-' : (rec.company_name || '-')}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#4b5563', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {isWaitingStatus(rec.status) ? '-' : (rec.booking_id || '-')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1f2937', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {rec.room_number || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: '#4b5563', py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              {rec.created_at ? dayjs(rec.created_at).format("DD-MM-YYYY hh:mm A") : "-"}
                            </TableCell>
                            <TableCell sx={{ py: 0.8, px: 1.2, whiteSpace: 'nowrap' }}>
                              <Chip
                                label={rec.status || 'waiting'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.64rem',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  px: 0.5,
                                  borderRadius: 1,
                                  ...getStatusChipStyle(rec.status)
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.8, px: 1.2 }}>
                              {rec.images && rec.images.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                                  {rec.images.map((img, i) => {
                                    const isAdded = existingImages.includes(img);
                                    return (
                                      <Tooltip key={i} title={isAdded ? "Added to new entry ✓" : "Click or Drag down to add to new entry"}>
                                        <Box
                                          draggable
                                          onDragStart={(e) => handleDragStart(e, img)}
                                          onClick={() => addExistingImage(img)}
                                          sx={{
                                            position: 'relative',
                                            cursor: 'grab',
                                            display: 'inline-block',
                                            '&:active': { cursor: 'grabbing' }
                                          }}
                                        >
                                          <Box
                                            component="img"
                                            src={getImageUrl(img)}
                                            sx={{
                                              width: 36,
                                              height: 36,
                                              borderRadius: 1,
                                              objectFit: 'cover',
                                              border: isAdded ? '2px solid #2e7d32' : '1px solid #d0d7de',
                                              opacity: isAdded ? 0.7 : 1,
                                              transition: 'all 0.15s ease-in-out',
                                              '&:hover': { transform: 'scale(1.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }
                                            }}
                                          />
                                          {isAdded && (
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: -4,
                                                right: -4,
                                                bgcolor: '#2e7d32',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: 14,
                                                height: 14,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '9px',
                                                fontWeight: 'bold',
                                                boxShadow: 1
                                              }}
                                            >
                                              ✓
                                            </Box>
                                          )}
                                        </Box>
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.68rem' }}>No images</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  mt: 2,
                  mb: 1,
                  p: 0.5,
                  borderRadius: 2,
                  border: isDragOver ? '2px dashed #1976d2' : '1px solid transparent',
                  bgcolor: isDragOver ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.2s ease'
                }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenSourceDialog(true)}
                  sx={{
                    py: isMobile ? 1 : 1.4,
                    borderStyle: 'dashed',
                    borderWidth: isDragOver ? 2 : 1.5,
                    fontSize: isMobile ? '0.82rem' : '0.9rem',
                    textTransform: 'none',
                    borderColor: isDragOver ? 'primary.main' : totalImagesCount > 0 ? 'primary.main' : '#ccc',
                    bgcolor: isDragOver ? '#bbdefb' : totalImagesCount > 0 ? '#e3f2fd' : 'transparent',
                    color: totalImagesCount > 0 ? 'primary.main' : 'text.secondary',
                    '&:hover': { borderStyle: 'dashed', bgcolor: '#e3f2fd' }
                  }}
                >
                  {isDragOver ? "📥 Drop Image Here to Add" : editId ? `Add More Images (${totalImagesCount}/6)` : `Select Guest Images or Drag Saved Images Here (${totalImagesCount}/6)`}
                </Button>

                {/* Hidden File Inputs */}
                <input
                  type="file"
                  hidden
                  ref={cameraInputRef}
                  onChange={(e) => {
                    handleFileChange(e);
                    setOpenSourceDialog(false);
                  }}
                  accept="image/*"
                  capture="environment"
                />
                <input
                  type="file"
                  multiple
                  hidden
                  ref={galleryInputRef}
                  onChange={(e) => {
                    handleFileChange(e);
                    setOpenSourceDialog(false);
                  }}
                  accept="image/*"
                />

                {/* Selected/Existing Image Previews */}
                {(images.length > 0 || existingImages.length > 0) && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, overflowX: 'auto', py: 0.5, px: 0.5 }}>
                    {/* Existing Images */}
                    {existingImages.map((img, idx) => (
                      <Box key={`ex-${idx}`} sx={{ position: 'relative', minWidth: 52 }}>
                        <Box
                          component="img"
                          src={getImageUrl(img)}
                          sx={{
                            width: 52, height: 52, borderRadius: 1, objectFit: 'cover',
                            border: '1px solid #ddd', cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                          onClick={() => setOpenImage(getImageUrl(img))}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); removeExistingImage(idx); }}
                          sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 18, height: 18, p: 0 }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 1, left: 1, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 0.4, borderRadius: 0.5, fontSize: '7px' }}>Saved</Typography>
                      </Box>
                    ))}

                    {/* New Uploads */}
                    {images.map((file, idx) => (
                      <Box key={`new-${idx}`} sx={{ position: 'relative', minWidth: 52 }}>
                        <Box
                          component="img"
                          src={URL.createObjectURL(file)}
                          sx={{
                            width: 52, height: 52, borderRadius: 1, objectFit: 'cover',
                            border: '1.5px solid', borderColor: 'primary.main',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                          onClick={() => setOpenImage(URL.createObjectURL(file))}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); removeSelectedImage(idx); }}
                          sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 18, height: 18, p: 0 }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 1, left: 1, bgcolor: '#1976d2', color: 'white', px: 0.4, borderRadius: 0.5, fontSize: '7px' }}>New</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Validation status indicator */}
              {!loading && (
                <Box sx={{ mt: 1, mb: 0.5, textAlign: 'center' }}>
                  {formData.phone_number.length < 10 ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Enter 10-digit phone number
                    </Typography>
                  ) : !formData.room_number ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Select a room number
                    </Typography>
                  ) : totalImagesCount === 0 ? (
                    <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Upload at least 1 guest image to enable Save (max 6)
                    </Typography>
                  ) : totalImagesCount > 6 ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.72rem' }}>
                      ⚠️ Maximum 6 images allowed (currently selected {totalImagesCount})
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '0.72rem' }}>
                      ✓ All details complete! ({totalImagesCount}/6 images) Click below to submit.
                    </Typography>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                color={editId ? "success" : "primary"}
                variant="contained"
                fullWidth
                disabled={!isFormValid || loading}
                size="medium"
                sx={{
                  mt: 1,
                  py: 1.1,
                  fontWeight: 700,
                  borderRadius: 1.5,
                  fontSize: isMobile ? '0.88rem' : '0.95rem',
                  textTransform: 'none',
                  opacity: (isFormValid && !loading) ? 1 : 0.45,
                  boxShadow: (isFormValid && !loading) ? (editId ? '0 4px 12px rgba(46,125,50,0.35)' : '0 4px 12px rgba(25,118,210,0.35)') : 'none',
                  transition: 'all 0.3s ease',
                  '&.Mui-disabled': {
                    bgcolor: '#c2c2c2',
                    color: '#ffffff',
                    opacity: 0.45
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : (editId ? "Update Submission" : "Save Submission")}
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Image Preview Modal */}
        <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'visible' } }}>
          <IconButton onClick={() => setOpenImage(null)} sx={{ position: 'absolute', right: -12, top: -12, bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
            {openImage && <Box component="img" src={openImage} sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 1, objectFit: 'contain' }} />}
          </DialogContent>
          <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={() => setOpenImage(null)} sx={{ borderRadius: 1.5, textTransform: 'none', px: 3, py: 0.6, fontSize: '0.85rem' }}>Close</Button>
          </Box>
        </Dialog>

        {/* Upload Source Selection Dialog */}
        <Dialog
          open={openSourceDialog}
          onClose={() => setOpenSourceDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              width: '90%',
              maxWidth: 300,
              p: 1
            }
          }}
        >
          <DialogContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
              Upload Image
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  onClick={() => cameraInputRef.current?.click()}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #d0d7de',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1976d2' }
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>📷</Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>Camera</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  onClick={() => galleryInputRef.current?.click()}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #d0d7de',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1976d2' }
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>📁</Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', textTransform: 'uppercase' }}>Gallery</Typography>
                </Box>
              </Grid>
            </Grid>
            <Button
              fullWidth
              size="small"
              onClick={() => setOpenSourceDialog(false)}
              sx={{ mt: 2, textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Cancel
            </Button>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
